import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { extractApiErrorMessage } from 'src/app/services/barcode.util';
import { GlobalCategoryService } from 'src/app/services/global-category.service';

@Component({
  selector: 'app-global-category-form-dialog',
  templateUrl: './global-category-form-dialog.component.html',
  styleUrls: ['./global-category-form-dialog.component.scss']
})
export class GlobalCategoryFormDialogComponent implements OnInit {

  @Input() category: any = null;
  /** Pre-select parent when adding a child from the tree. */
  @Input() parentCategoryId: string | null = null;
  @Input() allCategories: any[] = [];

  form!: FormGroup;
  saving = false;

  constructor(
    private dialogRef: NbDialogRef<GlobalCategoryFormDialogComponent>,
    private globalCategoryService: GlobalCategoryService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      slug: new FormControl(''),
      description: new FormControl(''),
      parentCategory: new FormControl(this.parentCategoryId || ''),
      displayOrder: new FormControl(0, [Validators.min(0)]),
      isVisible: new FormControl(true),
      status: new FormControl(1)
    });

    if (this.category) {
      const parent = this.category.parentCategory;
      const parentId = typeof parent === 'object' && parent?._id ? parent._id : (parent || '');
      this.form.patchValue({
        name: this.category.name || '',
        slug: this.category.slug || '',
        description: this.category.description || '',
        parentCategory: parentId || '',
        displayOrder: this.category.displayOrder ?? 0,
        isVisible: this.category.isVisible !== false,
        status: this.category.status ?? 1
      });
    }
  }

  get parentOptions(): any[] {
    const selfId = this.category?._id;
    const excluded = selfId ? this.collectDescendantIds(selfId) : new Set<string>();
    if (selfId) {
      excluded.add(String(selfId));
    }
    return this.allCategories.filter((c) => !excluded.has(String(c._id)));
  }

  private collectDescendantIds(rootId: string): Set<string> {
    const ids = new Set<string>();
    const walk = (parentId: string) => {
      for (const c of this.allCategories) {
        const p = c.parentCategory;
        const pid = p == null ? null : (typeof p === 'object' ? p._id : p);
        if (pid != null && String(pid) === String(parentId)) {
          const id = String(c._id);
          if (!ids.has(id)) {
            ids.add(id);
            walk(id);
          }
        }
      }
    };
    walk(String(rootId));
    return ids;
  }

  indentLabel(cat: any): string {
    const depth = cat.depth ?? 0;
    const pad = '\u00a0\u00a0'.repeat(depth);
    return pad + (cat.path || cat.name);
  }

  buildPayload(): any {
    const v = this.form.value;
    const parent = v.parentCategory || null;
    return {
      name: (v.name || '').trim(),
      slug: (v.slug || '').trim() || undefined,
      description: v.description?.trim() || null,
      parentCategory: parent,
      displayOrder: v.displayOrder ?? 0,
      isVisible: !!v.isVisible,
      status: v.status === 0 ? 0 : 1
    };
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastr.error('Please fill in all required fields');
      return;
    }
    const payload = this.buildPayload();
    this.saving = true;
    this.spinner.show();

    const request$ = this.category
      ? this.globalCategoryService.updateCategory(this.category._id, payload)
      : this.globalCategoryService.createCategory(payload);

    request$.subscribe({
      next: () => {
        this.spinner.hide();
        this.saving = false;
        this.toastr.success(this.category ? 'Category updated' : 'Category created');
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.spinner.hide();
        this.saving = false;
        this.toastr.error(extractApiErrorMessage(err, 'Save failed'));
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
